
import React, { useState, useEffect, useCallback, useRef } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Flag from 'react-country-flag'; // npm install react-country-flag

export default function Hms() {
  const API_URL = 'https://api.securxperts.com:8000';
  const ENDPOINT = '/forms/hms';

  /* -------------------- ENHANCED HMS CONTACT FORM WITH COUNTRY DROPDOWN ----------------------- */
  const ContactForm = () => {
    const [form, setForm] = useState({
      name: '',
      email: '',
      country_code: '',
      phoneno: '',
      purpose: '',
      branch_type: '',
      type_of: '',
      services: '',
      no_of_beds: '',
      total_logins: '',
      product_taking_time: '',
      heard_from: '',
    });

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [isCountryOpen, setIsCountryOpen] = useState(false);
    const [countrySearch, setCountrySearch] = useState('');
    const dropdownRef = useRef(null);

    // Input filters
    const nameFilter = useCallback((value) => /^[A-Za-z\s]*$/.test(value) ? value.slice(0, 50) : form.name, [form.name]);
    const phoneFilter = useCallback((value) => /^\d*$/.test(value) ? value.slice(0, 15) : form.phoneno, [form.phoneno]);
    const bedsFilter = useCallback((value) => /^\d*$/.test(value) ? value.slice(0, 4) : form.no_of_beds, [form.no_of_beds]);
    const loginsFilter = useCallback((value) => /^\d*$/.test(value) ? value.slice(0, 4) : form.total_logins, [form.total_logins]);

    // COUNTRIES WITH FLAGS
    const countries = [
      { code: '+91', iso: 'IN', name: 'India' },
      { code: '+1', iso: 'US', name: 'United States' },
      { code: '+44', iso: 'GB', name: 'United Kingdom' },
      { code: '+971', iso: 'AE', name: 'United Arab Emirates' },
      { code: '+61', iso: 'AU', name: 'Australia' },
      { code: '+852', iso: 'HK', name: 'Hong Kong' },
      { code: '+36', iso: 'HU', name: 'Hungary' },
      { code: '+354', iso: 'IS', name: 'Iceland' },
      { code: '+93', iso: 'AF', name: 'Afghanistan' },
      { code: '+355', iso: 'AL', name: 'Albania' },
    ];

    const filteredCountries = countries.filter(country =>
      country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
      country.code.toLowerCase().includes(countrySearch.toLowerCase())
    );

    const validateField = (name, value) => {
      switch (name) {
        case 'name':
          if (!value.trim()) return 'Please enter your full name';
          if (!/^[A-Za-z\s]+$/.test(value)) return 'Name can only contain letters and spaces';
          return '';

        case 'email':
          if (!value.trim()) return 'Email is required';
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address';
          return '';

        case 'country_code':
          if (!value.trim()) return 'Country code is required';
          if (!countries.some(c => c.code === value)) return 'Select valid country code from dropdown';
          return '';

        case 'phoneno':
          if (!value.trim()) return 'Phone number is required';
          if (!/^\d+$/.test(value)) return 'Phone number must contain only digits';
          if (value.length < 8 || value.length > 15) return 'Phone number must be 8-15 digits';
          return '';

        case 'purpose':
          if (!value.trim()) return 'Please specify purpose (Demo/Purchase/Upgrade)';
          return '';

        case 'branch_type':
          if (!value.trim()) return 'Please specify branch type';
          return '';

        case 'type_of':
          if (!value.trim()) return 'Please specify type of hospital';
          return '';

        case 'services':
          if (!value.trim()) return 'Please list your key services';
          return '';

        case 'no_of_beds':
          if (!value.trim()) return 'Please enter number of beds';
          const beds = parseInt(value);
          if (isNaN(beds) || beds < 1 || beds > 5000) return 'Enter valid number of beds (1-5000)';
          return '';

        case 'total_logins':
          if (!value.trim()) return 'Please enter total logins/users';
          const logins = parseInt(value);
          if (isNaN(logins) || logins < 1 || logins > 10000) return 'Enter valid number of users (1-10000)';
          return '';

        case 'product_taking_time':
          if (!value.trim()) return 'Please tell us when you plan to start using HMS';
          return '';

        case 'heard_from':
          if (!value.trim()) return 'Please tell us how you heard about us';
          return '';

        default:
          return '';
      }
    };

    const handleChange = (e) => {
      const { name, value } = e.target;
      let filteredValue = value;

      // Apply filters
      switch (name) {
        case 'name': filteredValue = nameFilter(value); break;
        case 'phoneno': filteredValue = phoneFilter(value); break;
        case 'no_of_beds': filteredValue = bedsFilter(value); break;
        case 'total_logins': filteredValue = loginsFilter(value); break;
      }

      setForm(prev => ({ ...prev, [name]: filteredValue }));
      const errorMsg = validateField(name, filteredValue);
      setErrors(prev => ({ ...prev, [name]: errorMsg }));
    };

    // Country selection
    const selectCountry = (code) => {
      setForm(prev => ({ ...prev, country_code: code }));
      setIsCountryOpen(false);
      setCountrySearch('');
      setErrors(prev => ({ ...prev, country_code: '' }));
    };

    // Outside click handler
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsCountryOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const validateAll = () => {
      const newErrors = {};
      Object.keys(form).forEach(key => {
        newErrors[key] = validateField(key, form[key]);
      });
      setErrors(newErrors);
      return Object.values(newErrors).every(err => err === '');
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!validateAll()) return;

      setSubmitting(true);
      try {
        const payload = { ...form };
        const resp = await fetch(`${API_URL}${ENDPOINT}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (resp.ok) {
          setShowSuccessPopup(true);
          setForm({
            name: '', email: '', country_code: '', phoneno: '',
            purpose: '', branch_type: '', type_of: '', services: '',
            no_of_beds: '', total_logins: '', product_taking_time: '', heard_from: '',
          });
          setErrors({});
        } else {
          console.error('HMS submit failed:', resp.status);
        }
      } catch (error) {
        console.error('HMS submit error:', error);
      } finally {
        setSubmitting(false);
      }
    };

    useEffect(() => {
      if (showSuccessPopup) {
        const timer = setTimeout(() => setShowSuccessPopup(false), 5000);
        return () => clearTimeout(timer);
      }
    }, [showSuccessPopup]);

    // COMPACT SUCCESS POPUP
    if (showSuccessPopup) {
      return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-xs w-full shadow-2xl relative p-4 animate-in fade-in zoom-in duration-200">
            <button
              onClick={() => setShowSuccessPopup(false)}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-xl font-bold p-1 hover:bg-gray-100 rounded-full transition-all w-8 h-8 flex items-center justify-center"
            >
              ×
            </button>
            <div className="text-center pt-2">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Success</h3>
              <p className="text-gray-600 text-sm">Your HMS enquiry has been submitted successfully.</p>
            </div>
          </div>
        </div>
      );
    }

    const hasAnyError = Object.values(errors).some(e => e);

    return (
      <form onSubmit={handleSubmit} className="max-w-3xl mx-auto bg-white/5 p-6 md:p-10 rounded-2xl shadow-lg" aria-label="HMS Enquiry Form">
        <h3 className="text-2xl md:text-3xl font-bold text-center mb-6 text-white">
          Hospital Management System – Enquiry Form
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { name: 'name', placeholder: 'Full Name *', span: 1 },
            { name: 'email', type: 'email', placeholder: 'Email *', span: 1 },
            
            // COUNTRY DROPDOWN
            { name: 'country_code', placeholder: 'Country Code *', span: 1, isDropdown: true },
            
            { name: 'phoneno', placeholder: 'Phone Number *', span: 1 },
            { name: 'purpose', placeholder: 'Purpose (Demo/Purchase/Upgrade) *', span: 2 },
            { name: 'branch_type', placeholder: 'Branch Type *', span: 2 },
            { name: 'type_of', placeholder: 'Type of Hospital *', span: 2 },
            { name: 'services', placeholder: 'Key Services *', span: 2 },
            { name: 'no_of_beds', type: 'number', placeholder: 'No. of Beds *', span: 1 },
            { name: 'total_logins', type: 'number', placeholder: 'Total Logins *', span: 1 },
            { name: 'product_taking_time', placeholder: 'When do you plan to start? *', span: 2 },
            { name: 'heard_from', placeholder: 'How did you hear about us? *', span: 2 },
          ].map((field) => (
            <div key={field.name} className={`${field.span === 2 ? 'md:col-span-2' : ''}`}>
              {field.isDropdown ? (
                // COUNTRY DROPDOWN WITH FLAGS
                <div className="relative" ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsCountryOpen(!isCountryOpen)}
                    className={`w-full px-4 py-3 rounded-lg border-2 border-white/20 bg-white/10 text-white placeholder-white/60 text-left flex justify-between items-center hover:border-white/40 focus:outline-none focus:border-[#2c75ff] focus:ring-1 focus:ring-[#2c75ff]/30 transition-all pr-10 ${
                      errors.country_code ? 'border-orange-400 ring-2 ring-orange-200/50' : ''
                    }`}
                  >
                    <span className="flex items-center gap-2 truncate flex-1">
                      {form.country_code ? (
                        <>
                          <Flag 
                            countryCode={countries.find(c => c.code === form.country_code)?.iso} 
                            svg className="w-5 h-3.5 flex-shrink-0 rounded" 
                          />
                          <span className="font-medium text-sm truncate">
                            {countries.find(c => c.code === form.country_code)?.name}
                          </span>
                          <span className="text-xs font-mono text-white/80 ml-1">
                            {form.country_code}
                          </span>
                        </>
                      ) : (
                        <span className="text-white/60 text-sm">Select Country Code</span>
                      )}
                    </span>
                    <svg className={`w-5 h-5 transition-transform text-white/60 ${isCountryOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {errors.country_code && (
                    <p className="mt-1 text-xs text-orange-400 font-medium pl-2">{errors.country_code}</p>
                  )}

                  {isCountryOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white/95 border border-white/20 rounded-xl shadow-2xl max-h-80 overflow-hidden backdrop-blur-sm">
                      <input
                        type="text"
                        value={countrySearch}
                        onChange={(e) => setCountrySearch(e.target.value)}
                        placeholder="Search country or code..."
                        className="w-full px-4 py-3 border-b border-gray-100 text-sm focus:outline-none focus:ring-1 focus:ring-[#2c75ff] bg-white"
                        autoFocus
                      />
                      <div className="max-h-64 overflow-auto">
                        {filteredCountries.length > 0 ? (
                          filteredCountries.map((country) => (
                            <button
                              key={country.code}
                              type="button"
                              onClick={() => selectCountry(country.code)}
                              className="w-full px-4 py-3 text-left hover:bg-[#2c75ff]/10 transition-all flex items-center gap-3 text-sm first:pt-2 last:pb-2 focus:outline-none focus:bg-[#2c75ff]/20"
                            >
                              <Flag countryCode={country.iso} svg className="w-6 h-4 flex-shrink-0 rounded" />
                              <span className="font-medium flex-1 min-w-0 truncate pr-4">{country.name}</span>
                              <span className="text-xs font-mono text-gray-700 bg-gray-100 px-2 py-1 rounded-full">
                                {country.code}
                              </span>
                            </button>
                          ))
                        ) : (
                          <div className="px-4 py-8 text-center text-sm text-gray-500">No countries found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // REGULAR INPUT
                <>
                  <input
                    name={field.name}
                    type={field.type || 'text'}
                    value={form[field.name]}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    required
                    className={`w-full p-3 rounded-lg bg-white/10 placeholder-white/60 text-white outline-none transition-all border-2 border-white/20 hover:border-white/40 focus:border-[#2c75ff] focus:ring-1 focus:ring-[#2c75ff]/30 ${
                      errors[field.name] ? 'border-orange-400 ring-2 ring-orange-200/50' : ''
                    }`}
                  />
                  {errors[field.name] && (
                    <p className="text-orange-400 text-xs mt-1 pl-1">{errors[field.name]}</p>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        <button
          type="submit"
          disabled={submitting || hasAnyError}
          className="bg-[#2c75ff] hover:bg-[#1d5bd9] disabled:bg-gray-400 disabled:cursor-not-allowed text-white py-3 rounded-xl font-semibold mt-6 shadow-md transition-all duration-300 text-lg w-full"
        >
          {submitting ? 'Submitting...' : 'Submit Enquiry'}
        </button>

        <p className="text-xs mt-4 text-white/60 text-center">
          Your details will remain confidential and used only for enquiry purposes.
        </p>
      </form>
    );
  };

  return (
    <div className="w-full mt-12 pt-10 overflow-hidden font-sans text-[#063852]">
    <div className="fixed top-0 left-0 w-full z-[9999]">
  <Navbar />
</div>


      {/* ================= HERO SECTION ================= */}
      <section className="relative w-full bg-white  px-6  mb-16">
        <div className="absolute top-0 right-0 w-[420px] h-[120px] bg-[#cae9ff] rounded-bl-full opacity-70 -z-10"></div>

        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          {/* Left Text */}
          <div>
            <img src="/img/52.png" alt="Logo" className="w-32 mb-6" />
            <p className="text-sm tracking-wide font-semibold text-[#2c75ff] mb-2">
              ALL-IN-ONE HEALTHCARE PLATFORM
            </p>

            <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-[38px]">
              Best Hospital Management System for Healthcare
            </h1>

            <p className="text-gray-600 mt-4 leading-relaxed mb-[128px]">
              An advanced, AI-enabled Hospital Management System built to accelerate complete
              digital transformation. Streamline clinical workflows, elevate patient care and
              optimize operations with secure, cloud-based automation.
            </p>

            {/* <div className="flex gap-4 mt-6">
              <button className="px-6 py-3 bg-[#2c75ff] text-white rounded-lg font-semibold hover:bg-[#1d5bd9] transition">
                Book a Demo
              </button>
              <button className="px-6 py-3 border border-[#2c75ff] text-[#2c75ff] rounded-lg font-semibold hover:bg-[#2c75ff] hover:text-white transition">
                Contact Us
              </button>
            </div> */}
          </div>

          {/* Right Image */}
          <div className="mb-[-32px]">
            <img
              src="/img/hmshero.jpg"
              alt="Hospital Dashboard"
              className="w-full drop-shadow-lg "
            />
          </div>
        </div>
      </section>

      {/* ================= VIDEO SECTION ================= */}
      <section className="w-full py-4 px-6 md:px-0 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto mb-10 text-center">
            <h2 className="text-3xl font-bold text-[#063852]">
              Watch MocDoc HMS in Action
            </h2>
            <p className="text-gray-600 mt-3">
              See how MocDoc HMS makes hospital management simple and efficient. Automate
              workflows, improve patient care and keep everything running smoothly with our
              AI-powered platform.
            </p>
          </div>

          <div className="relative rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl group">
            <video
              className="w-full block"
              src="/video/Arogya Narayan.mp4"
              muted
              playsInline
              autoPlay
              loop
              preload="auto"
              controls={false}
              key="lauratek-video"
              onClick={(e) => {
                const video = e.currentTarget;
                video.paused ? video.play() : video.pause();
              }}
            >
              Your browser does not support HTML5 video.
            </video>

            <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const video = e.currentTarget.closest("div").querySelector("video");
                  if (video.requestFullscreen) video.requestFullscreen();
                }}
                className="bg-black/70 hover:bg-black/90 text-white p-4 rounded-xl backdrop-blur-md transition transform hover:scale-110 shadow-2xl"
                title="Fullscreen"
              >
                <svg
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4-4l-5-5"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ================= WHY CHOOSE US ================= */}
      <section className="w-full py-20 px-6 md:px-16 bg-[#f8fbff]">
        <h2 className="text-3xl text-center font-bold mb-4">
          Why Choose Our Arogya Narayan for Digital Transformation?
        </h2>
        <p className="text-center text-gray-600 mb-12">
          Transforming Healthcare, Smarter & Faster
        </p>

        <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-10">
          <div className="bg-white p-6 shadow rounded-xl text-center">
            <h3 className="font-bold text-lg mb-2">1. End-to-End Digital Upgrade</h3>
            <p className="text-gray-600 text-sm">
              Upgrade your healthcare facility into a smart, automated and data-driven
              digital environment with next-gen Hospital Management solutions.
            </p>
          </div>

          <div className="bg-white p-6 shadow rounded-xl text-center">
            <h3 className="font-bold text-lg mb-2">2. Modern & User-Focused Interface</h3>
            <p className="text-gray-600 text-sm">
              A smart, responsive HMS interface built to boost staff productivity,
              streamline daily operations and improve overall workflow efficiency.
            </p>
          </div>

          <div className="bg-white p-6 shadow rounded-xl text-center">
            <h3 className="font-bold text-lg mb-2">3. Mobile-Ready Hospital Operations</h3>
            <p className="text-gray-600 text-sm">
              Access EMRs, billing, appointments and patient updates anytime, anywhere—
              ensuring uninterrupted operations across devices.
            </p>
          </div>

          <div className="bg-white p-6 shadow rounded-xl text-center">
            <h3 className="font-bold text-lg mb-2">4. Digital Healthcare Architecture</h3>
            <p className="text-gray-600 text-sm">
              Built to grow with you from single clinics to multi-hospital networks with
              seamless scalability and modular expansion.
            </p>
          </div>
        </div>
      </section>

      {/* ================= DIGITAL TRANSFORMATION ================= */}
      <section className="w-full py-20 px-6 md:px-16 relative">
        <div className="absolute left-0 top-20 w-[280px] h-[280px] bg-[#d9efff] rounded-r-full opacity-40 -z-10"></div>

        <h2 className="text-3xl font-bold mb-4">Digital Transformation Capabilities</h2>
        <p className="text-gray-600 mb-4 max-w-2xl">
          Smart, End-to-End Hospital Digital Solutions
        </p>

        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-10 items-center">
          <ul className="space-y-4 text-gray-700">
            <li>
              <span className="font-semibold">• Automated Operational Workflows</span>
              <br />
              Digitise administrative, clinical and financial processes with smart
              automation to cut manual work and gaps.
            </li>
            <li>
              <span className="font-semibold">• Complete Patient Pathway Management</span>
              <br />
              Manage the full patient journey Registration → Consultation → EMR →
              Diagnostics → Treatment → Billing → Discharge on one platform.
            </li>
            <li>
              <span className="font-semibold">• Integrated Hospital Operations Suite</span>
              <br />
              Comprehensive modules: Pharmacy, Lab, Radiology, Bed & Ward, Inventory,
              HR, Finance and more.
            </li>
            <li>
              <span className="font-semibold">• Regulatory & Compliance Built-In</span>
              <br />
              Ensure NABH, HIPAA and other healthcare compliance with integrated,
              audit-ready reporting.
            </li>
          </ul>

          <img
            src="/img/hms1.jpg"
            className="w-full drop-shadow-lg"
            alt="Digital Capability"
          />
        </div>
      </section>

      {/* ================= KEY FEATURES ================= */}
      <section className="w-full py-20 px-6 md:px-16 bg-[#f8fbff]">
        <h2 className="text-3xl font-bold text-center mb-4">
          Key Features of Our Digital Healthcare
        </h2>
        <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
          Next-gen intelligent, secure, and automated features to modernise workflows
          and enable a digital healthcare experience.
        </p>

        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-8">
          {[
            {
              title: "1. Personalised User Dashboards",
              text: "Role-based access ensures doctors, nurses, lab teams and administrators view only the tools and information they need.",
            },
            {
              title: "2. Seamless Dashboards",
              text: "Visual dashboards and flexible forms, along with approvals and processes, are tailored to match your hospital's operational structure.",
            },
            {
              title: "3. AI-Driven Scheduling & Planning",
              text: "AI intelligently optimises bed management, OT utilisation, and overall resource allocation, enhancing patient flow while reducing operational downtime effectively.",
            },
            {
              title: "4. Offline Operational Continuity",
              text: "Maintain uninterrupted hospital workflows even when the network is down, with auto-sync once connectivity returns.",
            },
            {
              title: "5. Seamless System Interoperability",
              text: "Effortless integration with EHR, PACS/RIS/LIS, payment systems, and national health portals, plus voice-enabled documentation for faster notes.",
            },
            {
              title: "6. Patient Engagement & Automation",
              text: "Automated WhatsApp and SMS notifications, instant NABH/NABL reporting and built-in compliance tools enhance patient communication and accreditation readiness.",
            },
          ].map((feature, i) => (
            <div
              key={i}
              className="
                group bg-white p-6 rounded-xl shadow
                border border-transparent
                relative overflow-hidden
                transition-all duration-300
                hover:shadow-xl hover:scale-[1.03]
              "
            >
              <span className="absolute top-0 left-0 w-0 h-[3px] bg-[#2c75ff] transition-all duration-300 group-hover:w-full"></span>
              <span className="absolute bottom-0 right-0 w-0 h-[3px] bg-[#2c75ff] transition-all duration-300 group-hover:w-full"></span>

              <h3 className="font-bold text-lg mb-2">{feature.title}</h3>
              <p className="text-gray-600 text-sm">{feature.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact section */}
      <section id="contact" className="py-16 bg-gradient-to-b from-white to-gray-900">
        <div className="max-w-6xl mx-auto px-6">
          <h3 className="text-3xl md:text-4xl font-extrabold text-center mb-6">
            Get in touch
          </h3>
          <p className="text-center text-black/70 mb-8">
            Have a question, need pricing, or want a customised HMS demo for your hospital? Share your
            details and our team will connect with you.
          </p>

          <ContactForm />
        </div>
      </section>

      <Footer />
    </div>
  );
}
